'use client'

import {
  ColorPicker,
  HStack,
  InputGroup,
  parseColor,
  Portal,
  Text,
} from '@chakra-ui/react'
import { LuCheck } from 'react-icons/lu'

import { NETWORK_COLOR_PALETTE } from './networkColorPalette'

type NetworkColorPickerFieldProps = {
  label: string
  hint?: string
  /** Hex color (#RRGGBB). Falls back to the first palette color when empty. */
  value?: string | null
  onChange: (hex: string) => void
  disabled?: boolean
}

const FALLBACK_COLOR = NETWORK_COLOR_PALETTE[0]

const toColor = (value?: string | null) => {
  try {
    return parseColor(value || FALLBACK_COLOR)
  } catch {
    return parseColor(FALLBACK_COLOR)
  }
}

/**
 * Primary-color picker for a network: free picking (area, hue, hex input,
 * eyedropper) plus on-brand swatches from {@link NETWORK_COLOR_PALETTE}.
 * Controlled with plain hex strings so it plugs into react-hook-form's
 * Controller without color-object plumbing. Alpha is intentionally not
 * exposed: the API only accepts 6-digit hex colors.
 */
export const NetworkColorPickerField = ({
  label,
  hint,
  value,
  onChange,
  disabled,
}: NetworkColorPickerFieldProps) => {
  return (
    <ColorPicker.Root
      value={toColor(value)}
      format="hsla"
      onValueChange={(e) => onChange(e.value.toString('hex'))}
      disabled={disabled}
      w="full"
    >
      <ColorPicker.HiddenInput tabIndex={-1} />
      <ColorPicker.Label>{label}</ColorPicker.Label>
      <ColorPicker.Control>
        <InputGroup
          startElementProps={{ pointerEvents: 'all' }}
          startElement={
            <ColorPicker.Trigger data-fit-content>
              <ColorPicker.ValueSwatch boxSize="4.5" />
            </ColorPicker.Trigger>
          }
        >
          <ColorPicker.Input />
        </InputGroup>
      </ColorPicker.Control>
      {hint ? (
        <Text textStyle="sm" color="fg.muted">
          {hint}
        </Text>
      ) : null}
      <Portal>
        <ColorPicker.Positioner>
          <ColorPicker.Content>
            <ColorPicker.Area />
            <HStack w="full">
              <ColorPicker.EyeDropper size="xs" variant="outline" />
              <ColorPicker.ChannelSlider channel="hue" flex="1" />
            </HStack>
            <ColorPicker.SwatchGroup>
              {NETWORK_COLOR_PALETTE.map((swatch) => (
                <ColorPicker.SwatchTrigger key={swatch} value={swatch}>
                  <ColorPicker.Swatch boxSize="4.5" value={swatch}>
                    <ColorPicker.SwatchIndicator>
                      <LuCheck />
                    </ColorPicker.SwatchIndicator>
                  </ColorPicker.Swatch>
                </ColorPicker.SwatchTrigger>
              ))}
            </ColorPicker.SwatchGroup>
          </ColorPicker.Content>
        </ColorPicker.Positioner>
      </Portal>
    </ColorPicker.Root>
  )
}
